import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle, ArrowLeft, ExternalLink, AlertTriangle } from "lucide-react";

import { routeTree } from "@/routeTree.gen";
import { listTicketProducts } from "@/lib/tickets.functions";

export const Route = createFileRoute("/_authenticated/admin/ticket-routes")({
  head: () => ({
    meta: [{ title: "ticket route check — admin" }],
  }),
  component: TicketRoutesAudit,
});

const SLUG_RE = /^[a-z0-9-]+$/;

/** Walk the generated route tree and collect every route id. */
function collectRouteIds(node: any, out: Set<string>) {
  if (!node) return;
  if (typeof node.id === "string") out.add(node.id);
  if (typeof node.path === "string" && node.path) out.add(node.path);
  const children = node.children;
  if (Array.isArray(children)) {
    for (const c of children) collectRouteIds(c, out);
  } else if (children && typeof children === "object") {
    for (const k of Object.keys(children)) collectRouteIds(children[k], out);
  }
}

function TicketRoutesAudit() {
  const fetchProducts = useServerFn(listTicketProducts);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-ticket-routes"],
    queryFn: () => fetchProducts(),
  });

  const ids = new Set<string>();
  collectRouteIds(routeTree as any, ids);

  const dynamicRouteId = "/tickets/$slug";
  const dynamicRegistered =
    ids.has(dynamicRouteId) || [...ids].some((i) => i.endsWith("/tickets/$slug"));

  const products = data?.products ?? [];
  const rows = products.map((p) => {
    const reasons: string[] = [];
    if (!dynamicRegistered) reasons.push("dynamic route /tickets/$slug not registered");
    if (!p.slug) reasons.push("missing slug");
    else if (!SLUG_RE.test(p.slug)) reasons.push("invalid slug format");
    return { slug: p.slug, name: p.name, ok: reasons.length === 0, reasons };
  });

  const missing = rows.filter((r) => !r.ok);
  const okCount = rows.length - missing.length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3" /> admin
      </Link>

      <h1 className="mt-4 text-3xl sm:text-4xl font-display font-extrabold lowercase">
        ticket route check
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        verifies every active ticket slug resolves through the registered{" "}
        <code className="px-1.5 py-0.5 rounded bg-secondary font-mono text-foreground">
          /tickets/$slug
        </code>{" "}
        route.
      </p>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <Stat label="active tickets" value={rows.length} />
        <Stat label="resolvable" value={okCount} tone="good" />
        <Stat label="missing" value={missing.length} tone={missing.length ? "bad" : "muted"} />
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
        {dynamicRegistered ? (
          <CheckCircle2 className="size-5 text-emerald-500 shrink-0" />
        ) : (
          <XCircle className="size-5 text-red-500 shrink-0" />
        )}
        <div className="text-sm">
          <div className="font-bold lowercase">
            dynamic route{" "}
            <span className="font-mono">/tickets/$slug</span>{" "}
            {dynamicRegistered ? "registered" : "MISSING"}
          </div>
          <div className="text-xs text-muted-foreground">
            {dynamicRegistered
              ? "every valid slug below will resolve."
              : "create src/routes/tickets.$slug.tsx — without it, every ticket link 404s."}
          </div>
        </div>
      </div>

      {isLoading && (
        <p className="mt-6 text-xs uppercase tracking-[0.2em] text-muted-foreground">loading…</p>
      )}

      {missing.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-red-500">
            <AlertTriangle className="size-3.5" /> missing / broken
          </div>
          <ul className="mt-3 space-y-2">
            {missing.map((r) => (
              <li
                key={r.slug || r.name}
                className="rounded-2xl border border-red-500/40 bg-red-500/5 p-4"
              >
                <div className="font-bold lowercase">{r.name}</div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  /tickets/{r.slug || "(no slug)"}
                </div>
                <ul className="mt-2 text-xs text-red-600 list-disc pl-5">
                  {r.reasons.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6">
        <div className="text-xs font-bold uppercase tracking-[0.22em] text-muted-foreground">
          all slugs
        </div>
        <ul className="mt-3 space-y-2">
          {rows.map((r) => (
            <li
              key={r.slug || r.name}
              className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4"
            >
              <div className="min-w-0 flex items-center gap-3">
                {r.ok ? (
                  <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                ) : (
                  <XCircle className="size-4 text-red-500 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="font-bold lowercase truncate">{r.name}</div>
                  <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">
                    /tickets/{r.slug}
                  </div>
                </div>
              </div>
              {r.ok && r.slug && (
                <Link
                  to="/tickets/$slug"
                  params={{ slug: r.slug }}
                  target="_blank"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] hover:bg-secondary"
                >
                  open <ExternalLink className="size-3" />
                </Link>
              )}
            </li>
          ))}
          {!isLoading && rows.length === 0 && (
            <li className="text-sm text-muted-foreground">no active ticket products.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: number;
  tone?: "good" | "bad" | "muted";
}) {
  const color =
    tone === "good"
      ? "text-emerald-500"
      : tone === "bad"
      ? "text-red-500"
      : "text-foreground";
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className={`text-3xl font-display font-extrabold ${color}`}>{value}</div>
      <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
